import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { muteVideoRef } from "@/lib/muteVideoRef";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useInView } from "@/hooks/useInView";
import { buttonVariants } from "@/components/flowly/infra/Button/Button";
import { PageContainer } from "@/components/flowly/PageContainer/PageContainer";
import { FLOWS_ANCHOR_ID, LANDING_IMAGE_FLOWS, LANDING_VIDEO_FLOWS, type LandingFlow } from "./constants";

const VIDEO_URL_REGEX = /\.(mp4|webm|mov)(\?|#|$)/i;
const isVideoUrl = (url: string) => VIDEO_URL_REGEX.test(url);

export function LandingFlowsShowcase() {
  const { t, isRTL } = useLanguage();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const { ref: imagesRef, isInView: imagesInView } = useInView();
  const { ref: videosRef, isInView: videosInView } = useInView();

  return (
    <section
      id={FLOWS_ANCHOR_ID}
      className="scroll-mt-24 py-12 md:py-20 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent"
    >
      <PageContainer width="wide">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight bg-gradient-to-r from-primary via-foreground to-primary bg-clip-text text-transparent">
            {t.landingFlows.title}
          </h2>
        </div>

        {/* Top row — image→image flows (3 cards on desktop). */}
        <SectionHeading>{t.landingFlows.imagesSectionTitle}</SectionHeading>
        <div
          ref={imagesRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 lg:gap-6 max-w-md md:max-w-none mx-auto"
        >
          {LANDING_IMAGE_FLOWS.map((flow, idx) => (
            <FlowCard
              key={flow.key}
              flow={flow}
              Arrow={Arrow}
              isInView={imagesInView}
              delayMs={idx * 100}
            />
          ))}
        </div>

        {/* Bottom row — image→video flows (2 cards on desktop, centered). The
            narrower max-width keeps each tile roughly the same size as the
            top-row cards instead of stretching to fill the wide container. */}
        <div className="mt-10 md:mt-14">
          <SectionHeading>{t.landingFlows.videosSectionTitle}</SectionHeading>
          <div
            ref={videosRef}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6 max-w-md md:max-w-3xl lg:max-w-4xl mx-auto"
          >
            {LANDING_VIDEO_FLOWS.map((flow, idx) => (
              <FlowCard
                key={flow.key}
                flow={flow}
                Arrow={Arrow}
                isInView={videosInView}
                delayMs={idx * 120}
              />
            ))}
          </div>
        </div>
      </PageContainer>
    </section>
  );
}

interface FlowCardProps {
  flow: LandingFlow;
  Arrow: typeof ArrowRight;
  isInView: boolean;
  delayMs: number;
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-foreground text-center mb-4 md:mb-6">
      {children}
    </h3>
  );
}

function FlowCard({ flow, Arrow, isInView, delayMs }: FlowCardProps) {
  const { t } = useLanguage();
  const flowText = t.landingFlows.flows[flow.nameKey];
  return (
    <Link
      to={flow.route}
      style={{ transitionDelay: `${delayMs}ms` }}
      className={cn(
        "group flex flex-col rounded-2xl md:rounded-3xl border border-border bg-card overflow-hidden shadow-md",
        "transition-all duration-500 hover:border-brand/40 hover:shadow-2xl hover:shadow-brand/10 md:hover:-translate-y-1 hover:ring-1 hover:ring-brand/20",
        isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6",
      )}
    >
      <BeforeAfter
        before={flow.beforeUrl}
        after={flow.afterUrl}
        beforeLabel={t.landingFlows.beforeLabel}
        afterLabel={t.landingFlows.afterLabel}
        Arrow={Arrow}
      />
      <div className="p-4 md:p-5 flex flex-col gap-2">
        <h3 className="text-lg md:text-lg lg:text-xl font-bold text-foreground leading-tight">
          {flowText.name}
        </h3>
        <p className="text-sm text-muted-foreground leading-snug">
          {flowText.description}
        </p>
        <span
          className={cn(
            buttonVariants({ variant: "accent", size: "sm" }),
            "self-start mt-1 shadow-lg shadow-brand/30 group-hover:shadow-xl group-hover:shadow-brand/40 group-hover:scale-[1.03]",
          )}
        >
          <Sparkles className="w-4 h-4" />
          {t.landingFlows.tryCta}
        </span>
      </div>
    </Link>
  );
}

interface BeforeAfterProps {
  before: string;
  after: string;
  beforeLabel: string;
  afterLabel: string;
  Arrow: typeof ArrowRight;
}

function BeforeAfter({ before, after, beforeLabel, afterLabel, Arrow }: BeforeAfterProps) {
  return (
    <div className="grid grid-cols-2 gap-0.5 relative">
      <Tile src={before} label={beforeLabel} />
      <Tile src={after} label={afterLabel} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 md:w-9 md:h-9 rounded-full bg-background border border-border shadow-md flex items-center justify-center pointer-events-none">
        <Arrow className="w-4 h-4 text-primary" />
      </div>
    </div>
  );
}

function Tile({ src, label }: { src: string; label: string }) {
  return (
    <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-br from-muted to-secondary">
      {isVideoUrl(src) ? (
        <video
          ref={muteVideoRef}
          src={src}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute top-2 md:top-2.5 start-2 md:start-2.5">
        <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-1 rounded-md bg-black/75 backdrop-blur-sm text-[10px] md:text-xs font-bold text-white uppercase tracking-wider shadow-sm ring-1 ring-white/10">
          {label}
        </span>
      </div>
    </div>
  );
}
